import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { PunksmarketIndexerBid } from '@/models/unified-market-bid';

export interface PunksmarketBidsListResponse {
  items: PunksmarketIndexerBid[];
  total: number;
  limit: number;
  offset: number;
}

export interface PunksmarketBidsMatchingPunkResponse {
  punkId: string;
  items: PunksmarketIndexerBid[];
  limit: number;
  offset: number;
}

export interface FetchPunksmarketBidsParams {
  active?: boolean;
  bidder?: string;
  limit?: number;
  offset?: number;
  sort?: string;
}

const DEFAULT_INDEXER = 'https://indexer.punksmarket.app';

@Injectable({ providedIn: 'root' })
export class PunksmarketBidIndexerService {
  constructor(private readonly http: HttpClient) {}

  isEnabled(): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
    );
  }

  getBaseUrl(): string | null {
    if (!this.isEnabled()) {
      return null;
    }
    const configured = environment.punksmarketBidIndexerUrl?.trim();
    if (configured) {
      return configured.replace(/\/$/, '');
    }
    const graphUri = environment.alternativeGraphURI || '';
    if (graphUri) {
      const local = graphUri.replace(/\/graphql$/i, '').replace(/\/$/, '');
      if (local) {
        return local;
      }
    }
    return DEFAULT_INDEXER;
  }

  fetchBids(
    params: FetchPunksmarketBidsParams = {},
  ): Observable<PunksmarketBidsListResponse> {
    const base = this.getBaseUrl();
    if (!base) {
      return of({ items: [], total: 0, limit: 0, offset: 0 });
    }

    let httpParams = new HttpParams();
    if (params.active !== undefined) {
      httpParams = httpParams.set('active', String(params.active));
    }
    if (params.bidder) {
      httpParams = httpParams.set('bidder', params.bidder);
    }
    if (params.limit != null) {
      httpParams = httpParams.set('limit', String(params.limit));
    }
    if (params.offset != null) {
      httpParams = httpParams.set('offset', String(params.offset));
    }
    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }

    return this.http
      .get<PunksmarketBidsListResponse>(`${base}/bids`, { params: httpParams })
      .pipe(
        catchError((err) => {
          console.error('[PunksmarketBidIndexer] fetchBids failed:', err);
          return of({ items: [], total: 0, limit: 0, offset: 0 });
        }),
      );
  }

  fetchBidsMatchingPunk(
    punkId: string | number,
    limit = 200,
  ): Observable<PunksmarketIndexerBid[]> {
    const base = this.getBaseUrl();
    if (!base) {
      return of([]);
    }

    const params = new HttpParams().set('limit', String(limit));
    return this.http
      .get<PunksmarketBidsMatchingPunkResponse>(
        `${base}/bids/matching/punk/${punkId}`,
        { params },
      )
      .pipe(
        map((res) =>
          (res.items ?? []).filter((b) => b.active !== false),
        ),
        catchError((err) => {
          console.error(
            '[PunksmarketBidIndexer] fetchBidsMatchingPunk failed:',
            err,
          );
          return of([]);
        }),
      );
  }

  fetchBidById(bidId: string): Observable<PunksmarketIndexerBid | null> {
    const base = this.getBaseUrl();
    if (!base) {
      return of(null);
    }
    return this.http
      .get<PunksmarketIndexerBid>(`${base}/bids/${bidId}`)
      .pipe(
        catchError((err) => {
          console.error('[PunksmarketBidIndexer] fetchBidById failed:', err);
          return of(null);
        }),
      );
  }
}
